<template>
  <div v-if="loaded">
    <div class="container">
      <div v-if="['unauthenticated', 'ready'].includes(status)">
        <h1 class="title">Join the Journey! Accept Your Invitation to collaborate.</h1>
        <p class="body">Welcome to Alabama's trip planning platform! We're thrilled you want to
          accept the invitation to help craft an unforgettable Alabama adventure. To accept and
          get started simply sign in with your existing account. New to our platform?
          Create an account in just a few steps and join the collaboration. Happy travels!
        </p>
      </div>
      <div v-if="status === 'declined'">
        <h1 class="title">{{ `You've declined ${requestor.name ? `${requestor.name.firstName ||
          requestor.name.lastName}`.trim() : requestor.email}'s
          invitation.`}}</h1>
        <p class="body">We noticed you declined the collaboration invite. But don't let that stop you from exploring the
          world of trip planning. Dive in and create your very own adventure today.</p>
      </div>
      <div v-if="['accepted', 'error'].includes(status)">
        <h1 class="title">It looks like that’s not a valid invitation.</h1>
        <p class="body">We noticed the collaboration invite no longer exists. But that shouldn't deter you from delving
          into
          the realm of trip planning. Embark on a journey and design your unique adventure today.</p>
      </div>
      <div v-if="!userId && ['accepted', 'declined'].includes(status)">
        <h3 class="subtitle">Sign Up & Start Planning</h3>
        <p class="body">Sign up now and get guided through your very own trip planning journey.</p>
      </div>
    </div>
    <!-- not logged in -->
    <div v-if="!userId || (!userId && status === 'error')" class="response-options">
      <div>
        <BlueButton :onClick="signUp">Register</BlueButton>
      </div>
      <div>
        <BlueButton :onClick="signIn">Sign In</BlueButton>
      </div>
    </div>
    <!-- Logged In -->
    <div v-if="userId && status === 'ready'" class="response-options">
      <div>
        <BlueButton :onClick="acceptInvitation">{{ accepting ? 'Processing...' : 'Accept'
        }}</BlueButton>
      </div>
      <div>
        <BlueButton :onClick="rejectInvitation">{{ declining ? 'Processing...' : 'Decline'
        }}</BlueButton>
      </div>
    </div>
    <!-- Already processed or not found -->
    <div v-if="userId && (['accepted', 'declined'].includes(status) || status === 'error')" class="response-options">
      <div>
        <BlueButton :onClick="goToMyTrips">My Trips</BlueButton>
      </div>
    </div>
    <b-modal id="modals" ref="modals" hide-header hide-footer hide-backdrop scrollable no-close-on-backdrop>
      <a href="#" @click="hideModal" class="icon-close modal-icon-close"></a>
      <SignIn v-if="showSignInModal" v-on:showSU="handleModalViews('su')"></SignIn>
      <SignUp v-if="showSignUpModal" v-on:showSI="handleModalViews('si')"></SignUp>
      <ForgotPassword v-if="showForgotPasswordModal"></ForgotPassword>
      <Confirmation :myEmail="newUserEmail" v-if="showConfirmationModal" v-on:sign-up-email="assignEmail">
      </Confirmation>
      <ForgotPasswordConfirmationModal :myEmail="newUserEmail" v-if="showForgotPasswordConfirmationModal"
        v-on:sign-up-email="assignEmail" />
    </b-modal>
  </div>
</template>

<script>
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { Component, Vue } from 'vue-property-decorator';
import { mapActions } from 'vuex';
import BlueButton from '../components/Common/BlueButton.vue';
import Confirmation from './ConfirmationModal.vue';
import ForgotPasswordConfirmationModal from './ForgotPasswordConfirmationModal.vue';
import ForgotPassword from './ForgotPasswordModal.vue';
import SignIn from './LoginModal.vue';
import SignUp from './SignUpModal.vue';

@Component({
  components: {
    Confirmation,
    ForgotPassword,
    ForgotPasswordConfirmationModal,
    SignIn,
    SignUp,
    BlueButton,
  },
  data() {
    return {
      collaborator: null,
      code: null,
      invitation: null,
      loaded: false,
      requestor: null,
      accepting: false,
      declining: false,
      status: null,
      trip: null,
      userId: null,
      visitor: null,
      email: null,
      isCorrectUser: true,
      newUserEmail: null,
      showSignInModal: false,
      showSignUpModal: false,
      showForgotPasswordConfirmationModal: false,
      showConfirmationModal: false,
      showForgotPasswordModal: false,

    };
  },
  async created() {
    this.code = this.$route.params.id;
    try {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      this.userId = await user.username.replace(/[@.]/g, '|');
      const response = await axios.get(`${process.env.VUE_APP_API2}visitors/${user.username}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}`,
        },
      });
      this.email = response.data.email;
    } catch (error) {
      this.userId = null;
    }


    const invite = await axios.get(`${process.env.VUE_APP_API2}invitation/${this.code}`)
      .then(response => response.data)
      .catch(() => ({
        invitation: null,
        trip: null,
        requestor: null,
        collaborator: null,
        status: 'unauthenticated',
      })); // default to not found if error

    if (invite) {
      this.invitation = invite.invitation;
      this.trip = invite.trip;
      this.requestor = invite.requestor;
      this.collaborator = invite.collaborator;

      if (this.invitation && ['new', 'pending'].includes(this.invitation.status)) {
        this.status = 'ready';
      } else if (this.invitation && ['accepted', 'declined'].includes(this.invitation.status)) {
        this.status = this.invitation.status;
      } else {
        this.status = 'error';
      }
      if (this.invitation && this.invitation.email !== this.email) {
        this.isCorrectUser = false;
      }
    }

    this.$root.$on('sign-up-email', (myEmail) => {
      this.assignEmail(myEmail);
    });
    this.$root.$on('showSU', () => {
      this.handleModalViews('su');
    });
    this.$root.$on('showSI', () => {
      this.handleModalViews('si');
    });
    this.$root.$on('showFP', () => {
      this.handleModalViews('fp');
    });
    this.$root.$on('showCM', () => {
      this.handleModalViews('cm');
    });
    this.$root.$on('showFPC', () => {
      this.handleModalViews('fpc');
    });

    this.loaded = true;
  },
  methods: {
    ...mapActions({
      loadMyTrips: 'myTrips/loadMyTrips',
    }),
    assignEmail(value) {
      this.newUserEmail = value;
    },
    signIn() {
      this.handleModalViews('si');
    },
    signUp() {
      this.handleModalViews('su');
    },
    hideModal() {
      this.$bvModal.hide('modals');
    },
    async respondToInvite(value) {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const jwt = await user.signInUserSession.idToken.jwtToken;
      const result = await axios.post(`${process.env.VUE_APP_API2}collaborateResponse`, JSON.stringify({
        code: this.code,
        response: value,
      }), {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      })
        .then(() => true)
        .catch((error) => {
          this.$bvToast.toast(error.response.data.message, {
            title: 'error',
            autoHideDelay: 3000,
            appendToast: this.append,
            variant: 'danger',
          });
          return false;
        });

      return result;
    },
    async notify(message) {
      this.$bvToast.toast(message, {
        title: 'Info',
        autoHideDelay: 3000,
        appendToast: this.append,
        variant: 'info',
      });
    },
    async acceptInvitation() {
      this.accepting = true;
      const result = await this.respondToInvite(true);
      if (result) {
        this.loadMyTrips();
        this.$router.push({ name: 'trip-details', params: { id: this.invitation.trip_id } });
      }
    },
    goToMyTrips() {
      this.$router.push({ name: 'mytrip-list' });
    },
    async rejectInvitation() {
      this.declining = true;
      const result = await this.respondToInvite(false);
      if (result) {
        this.status = 'declined';
      }
    },

    handleModalViews(value) {
      // Show SignInModal
      if (value === 'si') {
        this.showSignInModal = true;
        this.showSignUpModal = false;
        this.showForgotPasswordModal = false;
        this.showConfirmationModal = false;
        this.showForgotPasswordConfirmationModal = false;
      }
      // Show SignUpModal
      if (value === 'su') {
        this.showSignInModal = false;
        this.showSignUpModal = true;
        this.showForgotPasswordModal = false;
        this.showConfirmationModal = false;
        this.showForgotPasswordConfirmationModal = false;
      }
      // Show ForgotPasswordModal
      if (value === 'fp') {
        this.showSignInModal = false;
        this.showSignUpModal = false;
        this.showForgotPasswordModal = true;
        this.showConfirmationModal = false;
        this.showForgotPasswordConfirmationModal = false;
      }
      // Show ConfirmationModal
      if (value === 'cm') {
        this.showSignInModal = false;
        this.showSignUpModal = false;
        this.showForgotPasswordModal = false;
        this.showConfirmationModal = true;
        this.showForgotPasswordConfirmationModal = false;
      }
      // Show ForgetPasswordConfirmationModal
      if (value === 'fpc') {
        this.showSignInModal = false;
        this.showSignUpModal = false;
        this.showForgotPasswordModal = false;
        this.showConfirmationModal = false;
        this.showForgotPasswordConfirmationModal = true;
      }
      this.$bvModal.show('modals');
    },
  },
})

class InviteRespond extends Vue { }

export default InviteRespond;
</script>


<style scoped lang="scss">
.container {
  margin-top: 100px;
  margin: 100px auto 0;
  width: 35%;
  text-align: center;

  .title {
    color: #173945;
    font-family: "IdealSans-Bold";
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  .subtitle {
    color: #173945;
    font-family: IdealSans-Bold;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin-top: 24px;
  }

  .body {
    align-self: stretch;
    color: #173945;
    font-family: 'IdealSans-Book';
    font-size: 16px;
    font-style: normal;
    font-weight: 325;
    line-height: 125%;
    /* 20px */
  }


  @media screen and (max-width: 768px) {
    width: 80%;
    text-align: left;
    margin-top: 45px;
  }
}


.response-options {
  display: flex;
  justify-content: center;
  gap: var(--spacing-spacing-md, 24px);
  margin-top: var(--spacing-spacing-md, 36px);

  @media screen and (max-width: 768px) {
    display: flex;
    width: 80%;
    justify-content: flex-start;
    text-align: left;
    gap: var(--spacing-spacing-md, 24px);
    margin-top: var(--spacing-spacing-md, 36px);
    padding-left: 12%;
  }
}
</style>
